// CRM sayfaları için ortak, sade kurumsal UI parçaları.
// Görsel dil Finance modülü ve TMS Control Tower ile aynıdır; bileşenler oradan yeniden kullanılır.
import React from "react";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
    COLORS,
    cardSx,
    getTone,
    FinancePageHeader,
    FinanceActionButton,
    FinanceIconAction,
    FinanceKpiTile,
    FinanceCard,
    FinanceSectionHeader,
} from "../invoice/finance/shared/FinanceUiKit";
import { TableCard as ListTableCard } from "../../OceanTMS/oceanDashboardUi";

export { COLORS as CRM_COLORS, cardSx as crmCardSx, getTone as getCrmTone };
export const CrmPageHeader = FinancePageHeader;
export const CrmActionButton = FinanceActionButton;
export const CrmIconAction = FinanceIconAction;
export const CrmKpiTile = FinanceKpiTile;
export const CrmCard = FinanceCard;
export const CrmSectionHeader = FinanceSectionHeader;
export { TableCard as CrmTableCard, SegmentedTabs as CrmSegmented, ControlTowerPage as CrmTabbedPage, TruncatedText as ListTruncate } from "../../OceanTMS/oceanDashboardUi";

/** Liste sayfalarının sayfa kabuğu (üst/alt boşluklar tüm CRM sayfalarında aynı). */
export const CrmPage = ({ children }) => (
    <MDBox pt={3} pb={3} display="flex" flexDirection="column" gap={2.5}>
        {children}
    </MDBox>
);

/** Filtre alanları için ince kenarlıklı kart. */
export const CrmFilterBar = ({ children, sx = {} }) => (
    <Card sx={{ ...cardSx, overflow: "visible", ...sx }}>
        <MDBox p={2} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            {children}
        </MDBox>
    </Card>
);

/** Form sayfalarında form alanlarını saran kart. */
export const CrmFormCard = ({ title, subtitle, children, sx = {} }) => (
    <Card sx={{ ...cardSx, overflow: "visible", ...sx }}>
        <MDBox p={{ xs: 2, md: 3 }}>
            {title && <FinanceSectionHeader title={title} subtitle={subtitle} />}
            {children}
        </MDBox>
    </Card>
);

/** Tablo satırındaki düzenle / sil düğmeleri. */
export const CrmRowActions = ({ onEdit, onDelete, editTitle = "Edit", deleteTitle = "Delete", children }) => {
    const btn = (color, bg) => ({
        width: 32,
        height: 32,
        borderRadius: "8px",
        color,
        border: `1px solid ${COLORS.cardBorder}`,
        background: COLORS.white,
        "&:hover": { background: bg, borderColor: color },
        "& svg": { fontSize: "18px !important" },
    });
    return (
        <MDBox display="flex" alignItems="center" gap={0.75}>
            {onEdit && (
                <Tooltip title={editTitle}>
                    <IconButton onClick={onEdit} sx={btn(COLORS.blue, COLORS.blueLight)}>
                        <EditOutlined />
                    </IconButton>
                </Tooltip>
            )}
            {onDelete && (
                <Tooltip title={deleteTitle}>
                    <IconButton onClick={onDelete} sx={btn(COLORS.red, COLORS.redLight)}>
                        <DeleteOutline />
                    </IconButton>
                </Tooltip>
            )}
            {children}
        </MDBox>
    );
};

/** Başarı / hata / bilgi bildirimi (eski yeşil dolgulu kutular ve MDAlert yerine). */
export const CrmNotice = ({ children, tone = "green", sx = {} }) => {
    if (!children) return null;
    const t = getTone(tone);
    const Icon = tone === "red" ? ErrorOutline : tone === "green" ? CheckCircleOutline : InfoOutlined;
    return (
        <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            px={2}
            py={1.25}
            sx={{ borderRadius: "8px", background: t.bg, border: `1px solid ${t.border}`, color: t.color, ...sx }}
        >
            <Icon sx={{ fontSize: "20px !important", color: `${t.color} !important` }} />
            <MDTypography sx={{ fontSize: 13.5, fontWeight: 600, color: t.color }}>{children}</MDTypography>
        </MDBox>
    );
};

/* ---------- Ortak liste sayfası parçaları (Quoting Tables tasarımı) ---------- */

/** Satırdaki kayıt numarası / ad bağlantısı (mavi, kalın, üzerine gelince altı çizili). */
export const ListLink = ({ children, onClick, title }) => (
    <MDBox
        component="button"
        type="button"
        title={title}
        onClick={onClick}
        sx={{
            border: 0,
            p: 0,
            background: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 700,
            color: COLORS.blue,
            textAlign: "left",
            "&:hover": { textDecoration: "underline" },
        }}
    >
        {children}
    </MDBox>
);

/** İki satırlı hücre: üstte ana bilgi, altta gri ikincil bilgi. */
export const ListText = ({ primary, secondary, strong = false }) => (
    <MDBox minWidth={0}>
        <MDTypography sx={{ fontSize: 13, fontWeight: strong ? 600 : 500, color: COLORS.text, lineHeight: 1.4 }}>
            {primary === undefined || primary === null || primary === "" ? "—" : primary}
        </MDTypography>
        {secondary ? (
            <MDTypography sx={{ fontSize: 12, color: COLORS.subtext, lineHeight: 1.4 }}>{secondary}</MDTypography>
        ) : null}
    </MDBox>
);

/** Satır aksiyonları ile aynı görünümde tek ikon butonu (PDF, görüntüle, onayla...). */
export const ListIconButton = ({ title, onClick, children, tone = "navy", disabled = false }) => {
    const t = getTone(tone);
    return (
        <Tooltip title={title || ""}>
            <span>
                <IconButton
                    onClick={onClick}
                    disabled={disabled}
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        color: tone === "navy" ? COLORS.text : t.color,
                        border: `1px solid ${COLORS.cardBorder}`,
                        background: COLORS.white,
                        "&:hover": { background: tone === "navy" ? COLORS.muted : t.bg, borderColor: tone === "navy" ? COLORS.navy : t.color },
                        "& svg": { fontSize: "18px !important" },
                    }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
};

/** Sade durum etiketi (renkli nokta + metin). tone: green | red | amber | blue | purple | navy */
export const ListStatus = ({ label, tone = "navy" }) => {
    const t = getTone(tone);
    return (
        <MDBox
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: t.color,
                background: t.bg,
                border: `1px solid ${t.border}`,
            }}
        >
            <MDBox component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: t.color }} />
            {label || "—"}
        </MDBox>
    );
};

/** Liste sayfası kabuğu: başlık + (filtre) + tablo kartı. */
export const ListPage = ({ title, subtitle, actions, notice, filters, tableTitle, tableSummary, children, above }) => (
    <CrmPage>
        <CrmPageHeader title={title} subtitle={subtitle} actions={actions} />
        {notice}
        {above}
        {filters ? <CrmFilterBar>{filters}</CrmFilterBar> : null}
        {children !== undefined && tableTitle !== null ? (
            <ListTableCard title={tableTitle || `${title} list`} summary={tableSummary}>
                {children}
            </ListTableCard>
        ) : (
            children
        )}
    </CrmPage>
);

/** Filtre çubuğundaki küçük başlıklı alan. */
export const ListFilterField = ({ label, children, sx = {} }) => (
    <MDBox display="flex" flexDirection="column" gap={0.75} sx={sx}>
        <MDTypography sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.subtext, letterSpacing: "0.3px", textTransform: "uppercase" }}>
            {label}
        </MDTypography>
        {children}
    </MDBox>
);

/**
 * Yük tipi seçimi (FTL/LTL, FCL/LCL...): ikonlu, açıklamalı ve sayaçlı seçim kartları.
 * options: [{ value, label, caption, count, icon }]
 */
export const LoadTypeTabs = ({ options, value, onChange }) => (
    <MDBox
        display="grid"
        gridTemplateColumns={{ xs: "1fr", sm: `repeat(${options.length}, minmax(0, 1fr))` }}
        gap={1.5}
    >
        {options.map((opt) => {
            const active = opt.value === value;
            return (
                <MDBox
                    key={String(opt.value)}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    onClick={() => onChange(opt.value)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.75,
                        textAlign: "left",
                        borderRadius: "10px",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        background: active ? "#F5F8FF" : COLORS.white,
                        border: `1px solid ${active ? COLORS.navy : COLORS.cardBorder}`,
                        boxShadow: active ? `inset 0 0 0 1px ${COLORS.navy}` : "none",
                        transition: "border-color .15s ease, background .15s ease",
                        "&:hover": { borderColor: COLORS.navy },
                        "&:focus-visible": { outline: `2px solid ${COLORS.blue}`, outlineOffset: 2 },
                    }}
                >
                    <MDBox
                        width={40}
                        height={40}
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            borderRadius: "8px",
                            background: active ? COLORS.navy : "#EEF2F6",
                            color: active ? COLORS.white : COLORS.navy,
                            "& svg": { fontSize: "21px !important", color: "inherit" },
                        }}
                    >
                        {opt.icon}
                    </MDBox>
                    <MDBox flex={1} minWidth={0}>
                        <MDTypography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text, lineHeight: 1.3 }}>
                            {opt.label}
                        </MDTypography>
                        {opt.caption && (
                            <MDTypography sx={{ fontSize: 12, color: COLORS.subtext, lineHeight: 1.35 }}>{opt.caption}</MDTypography>
                        )}
                    </MDBox>
                    {opt.count !== undefined && (
                        <MDTypography
                            sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: active ? COLORS.navy : COLORS.subtext }}
                        >
                            {opt.count}
                        </MDTypography>
                    )}
                </MDBox>
            );
        })}
    </MDBox>
);
